import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { useEntityAction } from "@/lib/mutations";
import { useAuth } from "@/auth/auth";
import { PagedTable } from "@/components/PagedTable";
import { RowActions } from "@/components/RowActions";
import { TaskActions, TaskDriver } from "@/components/TaskManager";
import { DriverTelegramActions } from "@/components/DriverTelegramActions";
import { Button, Card, ErrorNote, StatusBadge } from "@/components/ui";
import { tStatus } from "@/i18n";
import { formatCompanyDateTime } from "@/lib/time";
import type { DriverGuide, OperationalTask } from "@/lib/types";

const TASK_STATUSES = [
  "unassigned", "assigned", "driver_confirmed", "in_progress", "completed",
  "handed_back", "cancelled",
];

const taskCol = createColumnHelper<OperationalTask>();
const driverCol = createColumnHelper<DriverGuide>();

export default function OperationsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canWrite = can("operations:write");
  const canAssign = can("operations:assign");
  const [tab, setTab] = useState<"tasks" | "drivers">("tasks");
  const [status, setStatus] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const tasks = usePaged<OperationalTask>(["op-tasks"], "/operational-tasks", {
    status: status || undefined,
    include_deleted: showDeleted ? "true" : undefined,
  });
  const drivers = usePaged<DriverGuide>(["drivers-guides"], "/drivers-guides", {
    include_deleted: showDeleted ? "true" : undefined,
  });
  const taskAction = useEntityAction([["op-tasks"]]);
  const driverAction = useEntityAction([["drivers-guides"]]);

  const taskColumns = useMemo(
    () => [
      taskCol.accessor("service", {
        header: () => t("fields.service"),
        cell: (c) => c.getValue() ?? "—",
      }),
      taskCol.accessor("hotel", {
        header: () => t("fields.hotel"),
        cell: (c) => c.getValue() ?? "—",
      }),
      taskCol.accessor("guest_count", {
        header: () => t("fields.guests"),
        cell: (c) => c.getValue() ?? "—",
      }),
      taskCol.accessor("pickup_time", {
        header: () => t("fields.pickup"),
        cell: (c) => (c.getValue() ? formatCompanyDateTime(c.getValue()) : "—"),
      }),
      taskCol.accessor("status", {
        header: () => t("fields.status"),
        cell: (c) => <StatusBadge status={c.getValue()} />,
      }),
      taskCol.accessor("booking_id", {
        header: () => t("fields.booking"),
        cell: (c) => (
          <Link
            className="text-sand hover:text-gold"
            to={`/bookings/${c.row.original.booking_number ?? c.getValue()}`}
          >
            {c.row.original.booking_number ?? t("common.open")}
          </Link>
        ),
      }),
      taskCol.display({
        id: "driver",
        header: () => t("operations.driver"),
        cell: (c) => (
          <TaskDriver
            task={c.row.original}
            invalidateKeys={[["op-tasks"], ["calendar"]]}
            canAssign={canAssign}
          />
        ),
      }),
      ...(canWrite
        ? [
            taskCol.display({
              id: "manage",
              header: () => t("operations.manage"),
              cell: (c) => {
                const id = c.row.original.id;
                return (
                  <TaskActions
                    task={c.row.original}
                    invalidateKeys={[["op-tasks"], ["calendar"]]}
                    canWrite={canWrite}
                    rowBusy={taskAction.isPending}
                    onDelete={() =>
                      taskAction.mutate(() => api.del(`/operational-tasks/${id}`))
                    }
                    onRestore={() =>
                      taskAction.mutate(() =>
                        api.post(`/operational-tasks/${id}/restore`),
                      )
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [t, canWrite, canAssign, taskAction],
  );

  const driverColumns = useMemo(
    () => [
      driverCol.accessor("name", { header: () => t("fields.name") }),
      driverCol.accessor("kind", {
        header: () => t("fields.kind"),
        cell: (c) => tStatus(c.getValue()),
      }),
      driverCol.display({
        id: "employment",
        header: () => t("drivers.employment"),
        cell: (c) => <DriverEmployment driver={c.row.original} canWrite={canWrite} />,
      }),
      driverCol.accessor("phone", {
        header: () => t("fields.phone"),
        cell: (c) => c.getValue() ?? "—",
      }),
      driverCol.accessor("languages", {
        header: () => t("fields.languages"),
        cell: (c) => (c.getValue() ?? []).join(", ") || "—",
      }),
      driverCol.accessor("status", {
        header: () => t("fields.status"),
        cell: (c) => <StatusBadge status={c.getValue()} />,
      }),
      driverCol.display({
        id: "telegram",
        header: () => t("drivers.telegram"),
        cell: (c) => (
          <DriverTelegramActions driver={c.row.original} canWrite={canWrite} />
        ),
      }),
      ...(canWrite
        ? [
            driverCol.display({
              id: "actions",
              header: () => t("common.actions"),
              cell: (c) => {
                const id = c.row.original.id;
                return (
                  <RowActions
                    deleted={!!c.row.original.deleted_at}
                    canWrite={canWrite}
                    busy={driverAction.isPending}
                    onDelete={() =>
                      driverAction.mutate(() => api.del(`/drivers-guides/${id}`))
                    }
                    onRestore={() =>
                      driverAction.mutate(() =>
                        api.post(`/drivers-guides/${id}/restore`),
                      )
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [t, canWrite, driverAction],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-lg font-semibold">{t("operations.title")}</h1>
        <div className="flex gap-1 text-sm">
          {(["tasks", "drivers"] as const).map((key) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={
                "rounded-md px-3 py-1 transition " +
                (tab === key
                  ? "bg-gold text-olive-900"
                  : "text-sand-muted hover:text-sand")
              }
            >
              {key === "tasks" ? t("operations.tasks") : t("operations.drivers")}
            </button>
          ))}
        </div>
        {canWrite && (
          <label className="ml-auto flex items-center gap-1.5 text-sm text-sand-muted">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked);
                tasks.resetPage();
                drivers.resetPage();
              }}
            />
            {t("common.showDeleted")}
          </label>
        )}
      </div>

      {tab === "tasks" ? (
        <PagedTable
          columns={taskColumns}
          data={tasks.items}
          query={tasks.query}
          total={tasks.total}
          page={tasks.page}
          pageCount={tasks.pageCount}
          onPage={tasks.setPage}
          empty={t("operations.noTasks")}
          toolbar={
            <select
              className="rounded-md border border-olive-600 bg-olive-800 px-2 py-1 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                tasks.resetPage();
              }}
            >
              <option value="">{t("common.allStatuses")}</option>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {tStatus(s)}
                </option>
              ))}
            </select>
          }
        />
      ) : (
        <div className="space-y-4">
          {canWrite && <NewDriverForm />}
          <PagedTable
            columns={driverColumns}
            data={drivers.items}
            query={drivers.query}
            total={drivers.total}
            page={drivers.page}
            pageCount={drivers.pageCount}
            onPage={drivers.setPage}
            empty={t("operations.noDrivers")}
          />
        </div>
      )}
    </div>
  );
}

function NewDriverForm() {
  const { t } = useTranslation();
  const create = useEntityAction([["drivers-guides"]], { inlineError: true });
  const blank = { name: "", kind: "driver", phone: "", languages: "", is_staff: false };
  const [f, setF] = useState(blank);

  return (
    <Card>
      <div className="mb-2 text-sm font-semibold">
        {t("common.new")} · {t("operations.drivers")}
      </div>
      <div className="grid gap-3 sm:grid-cols-5">
        <input
          className="rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none"
          placeholder={t("fields.name")}
          value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })}
        />
        <select
          className="rounded-md border border-olive-600 bg-olive-900 px-2 py-2 text-sm"
          value={f.kind}
          onChange={(e) => setF({ ...f, kind: e.target.value })}
        >
          <option value="driver">{tStatus("driver")}</option>
          <option value="guide">{tStatus("guide")}</option>
        </select>
        <select
          aria-label={t("drivers.employment")}
          className="rounded-md border border-olive-600 bg-olive-900 px-2 py-2 text-sm"
          value={f.is_staff ? "own" : "contractor"}
          onChange={(e) => setF({ ...f, is_staff: e.target.value === "own" })}
        >
          <option value="contractor">{t("drivers.contractor")}</option>
          <option value="own">{t("drivers.own")}</option>
        </select>
        <input
          className="rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none"
          placeholder={t("fields.phone")}
          value={f.phone}
          onChange={(e) => setF({ ...f, phone: e.target.value })}
        />
        <input
          className="rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none"
          placeholder={t("fields.languages")}
          value={f.languages}
          onChange={(e) => setF({ ...f, languages: e.target.value })}
        />
      </div>
      {create.isError && (
        <div className="mt-2">
          <ErrorNote message={(create.error as Error).message} />
        </div>
      )}
      <div className="mt-3">
        <Button
          loading={create.isPending}
          disabled={!f.name.trim()}
          onClick={() =>
            create.mutate(
              () =>
                api.post("/drivers-guides", {
                  name: f.name.trim(),
                  kind: f.kind,
                  is_staff: f.is_staff,
                  phone: f.phone || null,
                  languages: f.languages
                    ? f.languages.split(",").map((x) => x.trim()).filter(Boolean)
                    : null,
                }),
              { onSuccess: () => setF(blank) },
            )
          }
        >
          {t("common.create")}
        </Button>
      </div>
    </Card>
  );
}

/** «Свой / подрядчик» of one driver or guide, switchable in place. The owner's own
 * row is always «свой». Own = no per-trip pay, the client's money is ours at once. */
export function DriverEmployment({ driver, canWrite }: { driver: DriverGuide; canWrite: boolean }) {
  const { t } = useTranslation();
  // The picked value, held until the refetched row has it: the select is
  // controlled by the server's data, so without this it would snap back while
  // the save is in flight.
  const [picked, setPicked] = useState<boolean | null>(null);
  const saved = !!driver.is_staff || driver.is_owner;
  useEffect(() => setPicked(null), [saved]); // the refetched row has arrived
  const save = useEntityAction([["drivers-guides"]]);
  const own = picked ?? saved;
  if (!canWrite || driver.is_owner || driver.deleted_at) {
    return <span title={t("drivers.employmentHint")}>{own ? t("drivers.own") : t("drivers.contractor")}</span>;
  }
  return (
    <select
      aria-label={t("drivers.employment")}
      title={t("drivers.employmentHint")}
      className="rounded-md border border-olive-600 bg-olive-900 px-2 py-1 text-sm disabled:opacity-60"
      value={own ? "own" : "contractor"}
      disabled={save.isPending}
      onChange={(e) => {
        // Read the choice now: the request body is built later, after React has
        // put the controlled select back to the saved value — reading
        // e.target.value there sent the *old* value («свой» saved as «подрядчик»).
        const isStaff = e.target.value === "own";
        setPicked(isStaff);
        save.mutate(() => api.patch(`/drivers-guides/${driver.id}`, { is_staff: isStaff }), {
          onError: () => setPicked(null),
        });
      }}
    >
      <option value="contractor">{t("drivers.contractor")}</option>
      <option value="own">{t("drivers.own")}</option>
    </select>
  );
}
